import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { QueryClient } from "@tanstack/react-query";
import { Assistant, type StreamEvent } from "@/api/assistant";
import type { LivePart } from "@/components/assistant/Turns";
import { toast } from "@/lib/feedback";

// Chat state that must outlive the Home page, so leaving for Deals (or any
// other page) and coming back finds the chat exactly as it was: the same
// conversation, the same scroll position, an unsent draft, and a reply that kept
// streaming in the background.

export interface LiveReply {
  user: { text: string; skill?: string };
  parts: LivePart[];
}

export interface Draft {
  text: string;
  skill: string | null; // skill name
}

/** Drafts for a chat that hasn't been created yet use this key. */
export const NEW_CHAT = "new";

interface ChatState {
  /** The conversation Home reopens. Persisted, so it survives a reload. */
  lastChatId: string | null;
  setLastChatId: (id: string | null) => void;

  /** Unsent text + picked skill, per conversation (or NEW_CHAT). Persisted. */
  drafts: Record<string, Draft>;
  setDraft: (key: string, draft: Draft) => void;

  /** Replies currently streaming, per conversation. In memory only. */
  live: Record<string, LiveReply>;

  /** Scroll offset per conversation. In memory only. */
  scroll: Record<string, number>;
  setScroll: (id: string, top: number) => void;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      lastChatId: null,
      setLastChatId: (lastChatId) => set({ lastChatId }),

      drafts: {},
      setDraft: (key, draft) =>
        set((s) => {
          const drafts = { ...s.drafts };
          if (!draft.text && !draft.skill) delete drafts[key];
          else drafts[key] = draft;
          return { drafts };
        }),

      live: {},

      scroll: {},
      setScroll: (id, top) => set((s) => ({ scroll: { ...s.scroll, [id]: top } })),
    }),
    {
      name: "leadzeppelin-chat",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ lastChatId: s.lastChatId, drafts: s.drafts }),
    },
  ),
);

function updateLive(id: string, fn: (cur: LiveReply) => LiveReply) {
  useChatStore.setState((s) => {
    const cur = s.live[id];
    return cur ? { live: { ...s.live, [id]: fn(cur) } } : s;
  });
}

function applyEvent(id: string, e: StreamEvent) {
  if (e.type === "error") {
    toast.err(e.message);
    return;
  }
  updateLive(id, (cur) => {
    const parts = [...cur.parts];
    const last = parts[parts.length - 1];
    switch (e.type) {
      case "delta":
        if (last?.type === "text") parts[parts.length - 1] = { ...last, text: last.text + e.text };
        else parts.push({ type: "text", text: e.text });
        break;
      case "tool": {
        const i = parts.findIndex((p) => p.type === "live-tool" && p.id === e.id);
        const state = e.status === "start" ? "running" : e.status === "error" ? "error" : "done";
        if (i >= 0) parts[i] = { type: "live-tool", id: e.id, label: e.label, state };
        else parts.push({ type: "live-tool", id: e.id, label: e.label, state });
        break;
      }
      case "proposal":
        parts.push({ type: "proposal", action: e.action });
        break;
      case "document":
        parts.push({ type: "document", document: e.document });
        break;
      default:
        break;
    }
    return { ...cur, parts };
  });
}

/** Send a message and stream the reply into the store. Lives outside any
 *  component, so the stream keeps going if the user navigates away. */
export async function streamReply(
  qc: QueryClient,
  conversationId: string,
  text: string,
  skill: string | null,
): Promise<void> {
  useChatStore.setState((s) => ({
    live: { ...s.live, [conversationId]: { user: { text, skill: skill ?? undefined }, parts: [] } },
  }));
  try {
    let listed = false;
    await Assistant.send(conversationId, { text, skill }, (e) => {
      // The chat's title is set as the reply starts: refresh the sidebar's Recents once.
      if (!listed && e.type !== "start") {
        listed = true;
        void qc.invalidateQueries({ queryKey: ["assistant-conversations"] });
      }
      applyEvent(conversationId, e);
    });
  } catch (e) {
    toast.err(e instanceof Error ? e.message : String(e));
  } finally {
    // Swap the live reply for the stored one without a flash of nothing.
    await qc.refetchQueries({ queryKey: ["assistant-conversation", conversationId] });
    void qc.invalidateQueries({ queryKey: ["assistant-conversations"] });
    useChatStore.setState((s) => {
      const live = { ...s.live };
      delete live[conversationId];
      return { live };
    });
  }
}
